/**
 * FirstVisitHint — single-use, dismissible inline hint pill.
 *
 * Designed for teaching gestures that are easy to use but hard to discover
 * (long-press, drag-to-reorder, right-click menus, swipe actions). Shows
 * once per device — once dismissed (manually or by auto-fade) it never
 * shows again for that storage key.
 *
 * Usage:
 *   <FirstVisitHint
 *     storageKey="neurowave_timeline_hold_hint"
 *     onlyTouch                        // optional: hide on hover-capable devices
 *     icon={<Hand className="w-4 h-4" />}
 *   >
 *     <span className="font-medium">Hold a node</span> to delete it
 *   </FirstVisitHint>
 *
 * Tone: violet pill matching app accent. The "tip" prefix and dismiss-X are
 * built in so callers only supply the body copy + optional icon. Each hint
 * gets its own localStorage key so dismissing one doesn't suppress others.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';

const DISMISS_SUFFIX = '_dismissed';

// Read both the dismissal flag AND the touch-only gate during initial state
// computation so the component never flickers on (no setState-in-effect, no
// double-mount visibility race in React Strict Mode).
const computeInitialVisibility = (storageKey, onlyTouch) => {
  if (typeof window === 'undefined') return false;
  if (onlyTouch) {
    try {
      if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return false;
    } catch { /* ignore */ }
  }
  try {
    return localStorage.getItem(`${storageKey}${DISMISS_SUFFIX}`) !== '1';
  } catch {
    return false;
  }
};

export const FirstVisitHint = ({
  storageKey,
  children,
  icon = null,
  // When true, the hint only shows on touch-only devices (no fine hover).
  // Useful for gestures that are obvious on desktop (× button on hover) but
  // hidden on touch.
  onlyTouch = false,
  // Auto-fade after N ms. 0 disables auto-fade. The fade writes the dismiss
  // flag too, so the hint is treated as "seen" either way.
  autoFadeMs = 8000,
  className = '',
  'data-testid': dataTestId,
}) => {
  const fullKey = `${storageKey}${DISMISS_SUFFIX}`;
  const [visible, setVisible] = useState(() => computeInitialVisibility(storageKey, onlyTouch));

  // useCallback so the effect doesn't fight react-hooks/immutability — the
  // function identity is stable across renders for the same fullKey.
  const dismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(fullKey, '1'); } catch { /* ignore */ }
  }, [fullKey]);

  useEffect(() => {
    if (!visible || autoFadeMs <= 0) return undefined;
    const timer = setTimeout(dismiss, autoFadeMs);
    return () => clearTimeout(timer);
  }, [visible, autoFadeMs, dismiss]);

  if (!visible) return null;

  return (
    <div
      className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-violet-500/10 border border-violet-400/30 text-violet-100 text-xs transition-opacity duration-300 ${className}`}
      data-testid={dataTestId}
      role="status"
    >
      {icon && <span className="shrink-0 text-violet-200">{icon}</span>}
      <span className="flex-1">
        <span className="font-medium">Tip:</span> {children}
      </span>
      <button
        type="button"
        onClick={dismiss}
        className="shrink-0 w-5 h-5 rounded-full hover:bg-white/10 flex items-center justify-center text-white/60 hover:text-white"
        data-testid={dataTestId ? `${dataTestId}-dismiss` : undefined}
        aria-label="Dismiss hint"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export default FirstVisitHint;
